import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileSpreadsheet } from "lucide-react";
import {
  requisitionApi, rfqApi, evaluationApi, lpoApi, grnApi, ncrApi,
  prequalApi, supplierEvalApi, supplierApi, adminProcApi, procPdfApi,
} from "../api";
import { ProcError, ProcLoading, ProcPagination, ProcSection } from "../components/ProcurementLayout";
import { ProcStatusBadge } from "../components/ProcStatusBadge";
import { useAsyncData } from "../hooks";
import { downloadXls } from "../utils/export";

// ─── shared helpers ─────────────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

function fmtDate(v: string | null | undefined) {
  return v ? new Date(v).toLocaleDateString("en-KE") : "—";
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// A page-fetching function shaped like every list() in api.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Lister = (params: { page: number; size: number }) => Promise<{ content: any[]; totalElements: number }>;

const FETCH_CAP = 3000;

/** Wraps a paginated list() call into a "fetch (almost) everything" loader, since no
 *  procurement list endpoint supports year/month filters server-side — filtering has to
 *  happen client-side over a large batch instead. */
function fetchAll(list: Lister) {
  return async () => {
    const first = await list({ page: 0, size: 1 });
    const total = Math.min(first.totalElements, FETCH_CAP);
    if (total === 0) return [];
    const full = await list({ page: 0, size: total });
    return full.content;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Row = any;

type Column = {
  header: string;
  cell: (row: Row) => React.ReactNode;
  /** Value used for the Excel export — plain string/number, no JSX. */
  exportValue: (row: Row) => string | number | null | undefined;
};

type ModuleKey =
  | "requisitions" | "rfqs" | "evaluations" | "lpos" | "grns" | "ncrs"
  | "prequal" | "supplierEval" | "feedback" | "tenders" | "risks" | "delistings" | "suppliers";

type ModuleConfig = {
  label: string;
  viewBase: string;
  /** False for the two submodules (tenders, risks) that have no detail :id route — list/create only. */
  hasDetailView?: boolean;
  fetchAll: () => Promise<Row[]>;
  /** Field on each row holding its date, for year/month filtering. Omit if the module has no date field. */
  dateField?: string;
  statusField?: string;
  statusOptions?: { value: string; label: string }[];
  columns: Column[];
  pdfExport?: () => Promise<Blob>;
};

const statusBadgeCol = (header: string, field = "status"): Column => ({
  header,
  cell: (r) => <ProcStatusBadge value={r[field]} />,
  exportValue: (r) => r[field],
});

const MODULES: Record<ModuleKey, ModuleConfig> = {
  requisitions: {
    label: "Purchase Requisitions",
    viewBase: "/procurement/requisitions",
    fetchAll: fetchAll(requisitionApi.list),
    dateField: "date",
    statusField: "status",
    statusOptions: ["DRAFT", "SUBMITTED", "DEPT_HEAD_APPROVED", "FINANCE_APPROVED", "PROCUREMENT_REVIEWED", "MD_APPROVED", "REJECTED", "CANCELLED"]
      .map((v) => ({ value: v, label: v.replaceAll("_", " ") })),
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "Department", cell: (r) => r.departmentName, exportValue: (r) => r.departmentName },
      { header: "Purpose", cell: (r) => <span className="max-w-xs truncate block">{r.purpose}</span>, exportValue: (r) => r.purpose },
      statusBadgeCol("Status"),
      { header: "Date", cell: (r) => fmtDate(r.date), exportValue: (r) => fmtDate(r.date) },
    ],
  },
  rfqs: {
    label: "Requests for Quotation",
    viewBase: "/procurement/rfqs",
    fetchAll: fetchAll(rfqApi.list),
    dateField: "date",
    statusField: "status",
    statusOptions: ["DRAFT", "SENT", "CLOSED"].map((v) => ({ value: v, label: v })),
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "Linked Requisition", cell: (r) => r.requisitionSerialNo || "—", exportValue: (r) => r.requisitionSerialNo },
      { header: "Deadline", cell: (r) => fmtDate(r.deadline), exportValue: (r) => fmtDate(r.deadline) },
      statusBadgeCol("Status"),
      { header: "Date", cell: (r) => fmtDate(r.date), exportValue: (r) => fmtDate(r.date) },
    ],
  },
  evaluations: {
    label: "Quotation Evaluations",
    viewBase: "/procurement/evaluations",
    fetchAll: fetchAll(evaluationApi.list),
    dateField: "date",
    statusField: "status",
    statusOptions: ["DRAFT", "APPROVED", "REJECTED"].map((v) => ({ value: v, label: v })),
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "Linked RFQ", cell: (r) => r.rfqSerialNo || "—", exportValue: (r) => r.rfqSerialNo },
      { header: "Description", cell: (r) => <span className="max-w-xs truncate block">{r.description}</span>, exportValue: (r) => r.description },
      { header: "Recommended Supplier", cell: (r) => r.recommendedSupplierName || "—", exportValue: (r) => r.recommendedSupplierName },
      statusBadgeCol("Status"),
      { header: "Date", cell: (r) => fmtDate(r.date), exportValue: (r) => fmtDate(r.date) },
    ],
  },
  lpos: {
    label: "Local Purchase Orders",
    viewBase: "/procurement/lpos",
    fetchAll: fetchAll(lpoApi.list),
    dateField: "date",
    statusField: "status",
    statusOptions: ["DRAFT", "AUTHORIZED", "SENT", "CLOSED"].map((v) => ({ value: v, label: v })),
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "Supplier", cell: (r) => r.supplierName, exportValue: (r) => r.supplierName },
      { header: "Requisition", cell: (r) => r.requisitionSerialNo || "—", exportValue: (r) => r.requisitionSerialNo },
      { header: "Total", cell: (r) => (r.total != null ? Number(r.total).toLocaleString() : "—"), exportValue: (r) => r.total },
      statusBadgeCol("Status"),
      { header: "Date", cell: (r) => fmtDate(r.date), exportValue: (r) => fmtDate(r.date) },
    ],
  },
  grns: {
    label: "Goods Received Notes",
    viewBase: "/procurement/grns",
    fetchAll: fetchAll(grnApi.list),
    dateField: "date",
    statusField: "status",
    statusOptions: ["DRAFT", "ACCEPTED", "REJECTED"].map((v) => ({ value: v, label: v })),
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "LPO", cell: (r) => r.lpoSerialNo || "—", exportValue: (r) => r.lpoSerialNo },
      { header: "Received From", cell: (r) => r.receivedFrom, exportValue: (r) => r.receivedFrom },
      { header: "Delivery Note", cell: (r) => r.deliveryNoteNo, exportValue: (r) => r.deliveryNoteNo },
      statusBadgeCol("Status"),
      { header: "Date", cell: (r) => fmtDate(r.date), exportValue: (r) => fmtDate(r.date) },
    ],
  },
  ncrs: {
    label: "Non-Conformance Reports",
    viewBase: "/procurement/ncrs",
    fetchAll: fetchAll(ncrApi.list),
    dateField: "date",
    statusField: "status",
    statusOptions: ["OPEN", "RESOLVED", "CLOSED"].map((v) => ({ value: v, label: v })),
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "LPO", cell: (r) => r.lpoSerialNo || "—", exportValue: (r) => r.lpoSerialNo },
      { header: "Department", cell: (r) => r.departmentName || "—", exportValue: (r) => r.departmentName },
      { header: "Supplier", cell: (r) => r.supplierName, exportValue: (r) => r.supplierName },
      statusBadgeCol("Status"),
      { header: "Date", cell: (r) => fmtDate(r.date), exportValue: (r) => fmtDate(r.date) },
    ],
  },
  prequal: {
    label: "Supplier Prequalifications",
    viewBase: "/procurement/supplier-prequal",
    fetchAll: fetchAll(prequalApi.list),
    dateField: "date",
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "Supplier", cell: (r) => r.supplierName, exportValue: (r) => r.supplierName },
      { header: "Tech Total", cell: (r) => r.techTotal, exportValue: (r) => r.techTotal },
      { header: "Passed", cell: (r) => <ProcStatusBadge value={r.passed ? "PASSED" : "FAILED"} />, exportValue: (r) => (r.passed ? "Passed" : "Failed") },
      { header: "Date", cell: (r) => fmtDate(r.date), exportValue: (r) => fmtDate(r.date) },
    ],
  },
  supplierEval: {
    label: "Supplier Evaluations",
    viewBase: "/procurement/supplier-eval",
    fetchAll: fetchAll(supplierEvalApi.list),
    dateField: "date",
    statusField: "recommendation",
    statusOptions: ["APPROVED", "APPROVED_WITH_CONDITIONS", "UNDER_OBSERVATION", "DELISTED"]
      .map((v) => ({ value: v, label: v.replaceAll("_", " ") })),
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "Supplier", cell: (r) => r.supplierName, exportValue: (r) => r.supplierName },
      { header: "Period", cell: (r) => r.evaluationPeriod, exportValue: (r) => r.evaluationPeriod },
      { header: "Total Score", cell: (r) => r.totalScore ?? "—", exportValue: (r) => r.totalScore },
      statusBadgeCol("Recommendation", "recommendation"),
      { header: "Date", cell: (r) => fmtDate(r.date), exportValue: (r) => fmtDate(r.date) },
    ],
  },
  feedback: {
    label: "Department Feedback",
    viewBase: "/procurement/admin/feedback",
    fetchAll: fetchAll(adminProcApi.listFeedback),
    dateField: "formDate",
    statusField: "overallSatisfaction",
    statusOptions: ["SATISFIED", "NEUTRAL", "DISSATISFIED"].map((v) => ({ value: v, label: v })),
    pdfExport: procPdfApi.feedback,
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "Department", cell: (r) => r.departmentName || "—", exportValue: (r) => r.departmentName },
      { header: "Item/Service", cell: (r) => r.itemService, exportValue: (r) => r.itemService },
      { header: "Supplier", cell: (r) => r.supplierName, exportValue: (r) => r.supplierName },
      statusBadgeCol("Overall Satisfaction", "overallSatisfaction"),
      { header: "Date", cell: (r) => fmtDate(r.formDate), exportValue: (r) => fmtDate(r.formDate) },
    ],
  },
  tenders: {
    label: "Tender Opening Register",
    viewBase: "/procurement/admin/tenders",
    fetchAll: fetchAll(adminProcApi.listTenders),
    dateField: "dateReceived",
    hasDetailView: false,
    pdfExport: procPdfApi.tenderOpening,
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "Tender/RFQ No", cell: (r) => r.tenderRfqNo, exportValue: (r) => r.tenderRfqNo },
      { header: "Supplier", cell: (r) => r.supplierName, exportValue: (r) => r.supplierName },
      { header: "Date Received", cell: (r) => fmtDate(r.dateReceived), exportValue: (r) => fmtDate(r.dateReceived) },
      { header: "Time Received", cell: (r) => r.timeReceived, exportValue: (r) => r.timeReceived },
    ],
  },
  risks: {
    label: "Risk Register",
    viewBase: "/procurement/admin/risks",
    fetchAll: fetchAll(adminProcApi.listRisks),
    // No date field exists on RiskRegisterEntry — year/month filtering is intentionally
    // unavailable for this module.
    hasDetailView: false,
    statusField: "status",
    statusOptions: ["OPEN", "MITIGATED", "CLOSED"].map((v) => ({ value: v, label: v })),
    pdfExport: procPdfApi.riskRegister,
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "Risk Identified", cell: (r) => <span className="max-w-xs truncate block">{r.riskIdentified}</span>, exportValue: (r) => r.riskIdentified },
      { header: "Likelihood", cell: (r) => r.likelihood, exportValue: (r) => r.likelihood },
      { header: "Impact", cell: (r) => r.impact, exportValue: (r) => r.impact },
      { header: "Responsible Officer", cell: (r) => r.responsibleOfficer, exportValue: (r) => r.responsibleOfficer },
      statusBadgeCol("Status"),
    ],
  },
  delistings: {
    label: "Supplier Delistings",
    viewBase: "/procurement/admin/delistings",
    fetchAll: fetchAll(adminProcApi.listDelistings),
    dateField: "reviewDate",
    statusField: "status",
    statusOptions: ["PENDING", "APPROVED", "REJECTED"].map((v) => ({ value: v, label: v })),
    pdfExport: procPdfApi.delisting,
    columns: [
      { header: "Serial No", cell: (r) => r.serialNo, exportValue: (r) => r.serialNo },
      { header: "Supplier", cell: (r) => r.supplierName, exportValue: (r) => r.supplierName },
      { header: "Review Date", cell: (r) => fmtDate(r.reviewDate), exportValue: (r) => fmtDate(r.reviewDate) },
      statusBadgeCol("Status"),
      { header: "Remarks", cell: (r) => r.remarks || "—", exportValue: (r) => r.remarks },
    ],
  },
  suppliers: {
    label: "Supplier Master",
    viewBase: "/procurement/suppliers",
    fetchAll: fetchAll(supplierApi.list),
    dateField: "approvedDate",
    statusField: "status",
    statusOptions: ["ACTIVE", "PENDING", "SUSPENDED", "DELISTED"].map((v) => ({ value: v, label: v })),
    columns: [
      { header: "Code", cell: (r) => r.code, exportValue: (r) => r.code },
      { header: "Name", cell: (r) => r.name, exportValue: (r) => r.name },
      { header: "Category", cell: (r) => r.category, exportValue: (r) => r.category },
      statusBadgeCol("Status"),
      { header: "Approved Date", cell: (r) => fmtDate(r.approvedDate), exportValue: (r) => fmtDate(r.approvedDate) },
    ],
  },
};

const MODULE_ORDER: ModuleKey[] = [
  "requisitions", "rfqs", "evaluations", "lpos", "grns", "ncrs",
  "prequal", "supplierEval", "feedback", "tenders", "risks", "delistings", "suppliers",
];

const PAGE_SIZE = 20;

export default function ProcurementReportsPage() {
  const [moduleKey, setModuleKey] = useState<ModuleKey>("requisitions");
  const [status, setStatus] = useState("");
  const [year, setYear] = useState<number | "ALL">(new Date().getFullYear());
  const [fromMonth, setFromMonth] = useState<number | "">("");
  const [toMonth, setToMonth] = useState<number | "">("");
  const [page, setPage] = useState(0);
  const [exportingXls, setExportingXls] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [downloadingMaster, setDownloadingMaster] = useState(false);

  const config = MODULES[moduleKey];

  const { data: allRows, loading, error } = useAsyncData(() => config.fetchAll(), [moduleKey]);

  // Reset filters/page whenever the module changes — status options and date field differ per module.
  useEffect(() => {
    setStatus("");
    setFromMonth("");
    setToMonth("");
    setPage(0);
  }, [moduleKey]);

  useEffect(() => {
    setPage(0);
  }, [status, year, fromMonth, toMonth]);

  const availableYears = useMemo(() => {
    const years = new Set<number>([new Date().getFullYear()]);
    if (config.dateField && allRows) {
      for (const r of allRows) {
        const raw = r[config.dateField];
        if (raw) years.add(new Date(raw).getFullYear());
      }
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [allRows, config.dateField]);

  const filteredRows = useMemo(() => {
    if (!allRows) return [];
    return allRows.filter((r: Row) => {
      if (config.statusField && status && r[config.statusField] !== status) return false;
      if (config.dateField && (year !== "ALL" || fromMonth !== "" || toMonth !== "")) {
        const raw = r[config.dateField];
        if (!raw) return false;
        const d = new Date(raw);
        if (year !== "ALL" && d.getFullYear() !== year) return false;
        const m = d.getMonth() + 1;
        if (fromMonth !== "" && m < fromMonth) return false;
        if (toMonth !== "" && m > toMonth) return false;
      }
      return true;
    });
  }, [allRows, status, year, fromMonth, toMonth, config]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  async function exportExcel() {
    setExportingXls(true);
    try {
      const headers = config.columns.map((c) => c.header);
      const rows = filteredRows.map((r: Row) => config.columns.map((c) => c.exportValue(r)));
      await downloadXls(
        `${moduleKey}-report-${new Date().toISOString().slice(0, 10)}`,
        config.label,
        headers,
        rows,
      );
    } finally {
      setExportingXls(false);
    }
  }

  async function exportPdf() {
    if (!config.pdfExport) return;
    setExportingPdf(true);
    try {
      const blob = await config.pdfExport();
      downloadBlob(blob, `${moduleKey}-register-${new Date().toISOString().slice(0, 10)}.pdf`);
    } finally {
      setExportingPdf(false);
    }
  }

  async function downloadMasterReport() {
    setDownloadingMaster(true);
    try {
      const blob = await procPdfApi.masterReport();
      downloadBlob(blob, `procurement-master-report-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally {
      setDownloadingMaster(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Procurement Reports</h1>
        </div>
        <button
          type="button"
          onClick={downloadMasterReport}
          disabled={downloadingMaster}
          className="flex items-center gap-1.5 rounded-xl border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
        >
          <FileSpreadsheet className="h-4 w-4" />
          {downloadingMaster ? "Downloading…" : "Master Report (all modules, Excel)"}
        </button>
      </div>

      <ProcSection title="Filters">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Module</label>
            <select
              value={moduleKey}
              onChange={(e) => setModuleKey(e.target.value as ModuleKey)}
              className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {MODULE_ORDER.map((k) => (
                <option key={k} value={k}>{MODULES[k].label}</option>
              ))}
            </select>
          </div>

          {config.statusField && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Statuses</option>
                {config.statusOptions?.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          {config.dateField ? (
            <>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(e.target.value === "ALL" ? "ALL" : Number(e.target.value))}
                  className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="ALL">All Years</option>
                  {availableYears.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">From Month</label>
                <select
                  value={fromMonth}
                  onChange={(e) => setFromMonth(e.target.value ? Number(e.target.value) : "")}
                  className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Any</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">To Month</label>
                <select
                  value={toMonth}
                  onChange={(e) => setToMonth(e.target.value ? Number(e.target.value) : "")}
                  className="w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Any</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <div className="sm:col-span-3 flex items-end">
              <p className="text-xs text-slate-400 pb-2">This module has no date field — year/month filtering isn&apos;t available.</p>
            </div>
          )}
        </div>
      </ProcSection>

      {loading && <ProcLoading label={`Loading ${config.label}...`} />}
      {error && <ProcError message={error} />}

      {!loading && !error && allRows && (
        <ProcSection title={config.label}>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">
              {filteredRows.length.toLocaleString()} of {allRows.length.toLocaleString()} loaded
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={exportExcel}
                disabled={exportingXls || filteredRows.length === 0}
                className="flex items-center gap-1.5 rounded-xl border border-emerald-300 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              >
                <FileSpreadsheet className="h-4 w-4" />
                {exportingXls ? "Exporting…" : "Download Excel (filtered)"}
              </button>
              {config.pdfExport && (
                <button
                  type="button"
                  onClick={exportPdf}
                  disabled={exportingPdf}
                  className="flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  title="This register's PDF export covers the full register — filters aren't applied to it."
                >
                  <Download className="h-4 w-4" />
                  {exportingPdf ? "Downloading…" : "Download Full Register (PDF)"}
                </button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border bg-white shadow-sm overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  {config.columns.map((c) => (
                    <th key={c.header} className="px-4 py-3">{c.header}</th>
                  ))}
                  {config.hasDetailView !== false && <th className="px-4 py-3 text-right">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pagedRows.length === 0 && (
                  <tr><td colSpan={config.columns.length + 1} className="px-4 py-8 text-center text-slate-400">No records match these filters.</td></tr>
                )}
                {pagedRows.map((r: Row) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    {config.columns.map((c) => (
                      <td key={c.header} className="px-4 py-3 text-slate-700">{c.cell(r)}</td>
                    ))}
                    {config.hasDetailView !== false && (
                      <td className="px-4 py-3 text-right">
                        <Link to={`${config.viewBase}/${r.id}`} className="font-semibold text-emerald-700 hover:underline">View</Link>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3">
            <ProcPagination
              page={page}
              totalPages={totalPages}
              totalElements={filteredRows.length}
              onPage={setPage}
            />
          </div>
        </ProcSection>
      )}
    </div>
  );
}
